import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../../../components/DashboardLayout";
import Loader from "../../../components/Loader";
import { showErrorToastMsg } from "../../../utility/ToastMessage";
import { getPastPapersAPI } from "../../../services/portal/admin/pastPaperService";
import { RoutePaths } from "../../../utility/RoutePaths";

const PastPapersPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [pastPapers, setPastPapers] = useState([]);

  useEffect(() => {
    loadPastPapers();
  }, []);

  const loadPastPapers = async () => {
    setLoading(true);
    try {
      const res = await getPastPapersAPI();
      if (res && res.IsSuccessful && res.Data) {
        setPastPapers(res.Data);
      }
    } catch (error) {
      console.error("Error loading past papers:", error);
      showErrorToastMsg("Failed to load past papers");
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout title="Past Papers" subtitle="Browse available past papers">
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Loader />
        </div>
      ) : pastPapers.length === 0 ? (
        <div className="text-center py-16">
          <svg
            className="mx-auto w-16 h-16 text-gray-300 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <p className="text-gray-500 text-lg font-medium">
            No past papers available
          </p>
          <p className="text-gray-400 text-sm mt-1">
            Check back later for new content.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {pastPapers.map((paper) => (
            <div
              key={paper.Id}
              onClick={() =>
                navigate(
                  `${RoutePaths.STUDENT_PAST_PAPER_DETAILS_PAGE}?pastPaperId=${paper.Id}`,
                )
              }
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
            >
              <div className="flex items-start justify-between gap-3">
                {/* Document icon */}
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-primary-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>

                {/* Video badge */}
                {paper.VideoLink && (
                  <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 text-red-600 text-xs font-medium">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                    Video
                  </span>
                )}
              </div>

              <div className="mt-3">
                <h3 className="text-sm font-bold text-gray-900 line-clamp-2">
                  {paper.Title}
                </h3>
                {paper.Description && (
                  <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                    {paper.Description}
                  </p>
                )}
              </div>

              <div className="mt-4 flex items-center text-xs font-medium text-primary-600">
                View details
                <svg
                  className="w-3.5 h-3.5 ml-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
            </div>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
};

export default PastPapersPage;
